/**
 * 指挥系统多底图配置
 * - 图源目录（对齐 NECC：底图单选 + 注记叠加多选）
 * - 管理后台可配：启用开关、默认底图、Key、自定义瓦片地址
 * - 指挥页据此切换 Mapbox 样式或栅格瓦片
 */

import { getMapboxToken } from './mapboxToken';
import { pauseEarthSpinRender, isDashboardEarthDismissed } from './dashboard/earthSpinGate';
import {
  EARTH_SPACE_BG,
  applyEarthDeepSpaceLook,
  applyEarthSpaceBackground,
  whenMapStyleSettled
} from './dashboard/earthDeepSpace';
import {
  setActiveBasemapIdForCoord,
  getActiveBasemapIdForCoord,
  getBasemapDatum,
  convertDisplayLngLat,
  registerBasemapDatums,
  resolveBasemapDatum
} from './dashboard/basemapCoord';
import { restoreMapTerrain3d } from './dashboard/mapTerrainHelpers';
import { resolveRasterTilesForBasemap } from './dashboard/gcjRasterTiles';

const STORAGE_LAST_BASEMAP = 'dashboard_basemap_id_v1';
const STORAGE_LAST_OVERLAYS = 'dashboard_overlay_ids_v1';

/** 运行时 Key（可选，管理后台未填时回退） */
function getRuntimeKey(name) {
  if (typeof window === 'undefined') return '';
  const cfg = window.RUNTIME_CONFIG || {};
  return String(cfg[name] || '').trim();
}

/**
 * 内置图源目录（role: basemap | overlay）
 * type: mapbox-style | raster
 * datum: 底图显示基准。写 GCJ02 后点/线/面/DEM 自动纠偏；省略视为 WGS84
 *   叠加层（role: overlay）也写 datum，仅用于与底图基准不一致时提示（注记文字瓦片不做重采样）
 *   自定义影像（provider: custom）可在管理后台 customTileDatums 声明 WGS84 / GCJ02，默认 WGS84
 */
/** 顶栏主模式：二维 / 卫星 / 混合 */
export const BASEMAP_STYLE_MODES = ['streets', 'satellite', 'hybrid'];
/** 点混合后展开：高程 / 地形 */
export const BASEMAP_EXTRA_STYLE_MODES = ['outdoors', 'terrain'];
export const ALL_BASEMAP_STYLE_MODES = [...BASEMAP_STYLE_MODES, ...BASEMAP_EXTRA_STYLE_MODES];

/** 图源族：列表只显示这一级，模式由顶栏按钮选 */
export const BASEMAP_FAMILY_META = {
  tianditu: { label: '天地图', roadOverlayId: 'overlay-tianditu-cva' },
  gaode: { label: '高德地图', roadOverlayId: 'overlay-gaode-label' },
  google: { label: '谷歌地图', roadOverlayId: null },
  mapbox: { label: 'Mapbox', roadOverlayId: null },
  esri: { label: 'Esri高清影像', roadOverlayId: null }
};

export const MAP_PROVIDER_CATALOG = [
  // —— 底图（单选）——
  {
    id: 'tianditu-vec',
    label: '天地地图',
    role: 'basemap',
    type: 'raster',
    provider: 'tianditu',
    styleMode: 'streets',
    needsKey: 'tianditu',
    tileTemplate: 'tianditu:vec_w',
    defaultEnabled: true
  },
  {
    id: 'tianditu-ter',
    label: '天地地形',
    role: 'basemap',
    type: 'raster',
    provider: 'tianditu',
    styleMode: 'terrain',
    needsKey: 'tianditu',
    tileTemplate: 'tianditu:ter_w',
    defaultEnabled: true
  },
  {
    id: 'gaode-vec',
    label: '高德地图',
    role: 'basemap',
    type: 'raster',
    provider: 'gaode',
    datum: 'GCJ02',
    styleMode: 'streets',
    needsKey: null,
    tileTemplate: 'gaode:vec',
    defaultEnabled: true
  },
  {
    id: 'tianditu-img',
    label: '天地影像',
    role: 'basemap',
    type: 'raster',
    provider: 'tianditu',
    styleMode: 'satellite',
    needsKey: 'tianditu',
    tileTemplate: 'tianditu:img_w',
    defaultEnabled: true
  },
  {
    id: 'xingtu-img',
    label: '星图影像',
    role: 'basemap',
    type: 'raster',
    provider: 'custom',
    styleMode: 'satellite',
    needsKey: null,
    tileTemplate: 'custom:xingtu-img',
    defaultEnabled: false
  },
  {
    id: 'gaode-img',
    label: '高德影像',
    role: 'basemap',
    type: 'raster',
    provider: 'gaode',
    datum: 'GCJ02',
    styleMode: 'satellite',
    needsKey: null,
    tileTemplate: 'gaode:img',
    defaultEnabled: true
  },
  {
    // 高德无公开 DEM 样式：深色路网作为族内「地形图」
    id: 'gaode-ter',
    label: '高德地形',
    role: 'basemap',
    type: 'raster',
    provider: 'gaode',
    datum: 'GCJ02',
    styleMode: 'terrain',
    needsKey: null,
    tileTemplate: 'gaode:ter',
    defaultEnabled: true
  },
  {
    id: 'changguang-img',
    label: '长光影像',
    role: 'basemap',
    type: 'raster',
    provider: 'custom',
    styleMode: 'satellite',
    needsKey: null,
    tileTemplate: 'custom:changguang-img',
    defaultEnabled: false
  },
  {
    id: 'jilin-img',
    label: '吉林影像',
    role: 'basemap',
    type: 'raster',
    provider: 'custom',
    styleMode: 'satellite',
    needsKey: null,
    tileTemplate: 'custom:jilin-img',
    defaultEnabled: false
  },
  {
    id: 'google-vec',
    label: '谷歌地图',
    role: 'basemap',
    type: 'raster',
    provider: 'google',
    datum: 'GCJ02',
    styleMode: 'streets',
    needsKey: null,
    tileTemplate: 'google:m',
    defaultEnabled: true
  },
  {
    // 混合：卫星 + 谷歌标注（lyrs=y）
    id: 'google-img',
    label: '谷歌影像',
    role: 'basemap',
    type: 'raster',
    provider: 'google',
    datum: 'GCJ02',
    styleMode: 'hybrid',
    needsKey: null,
    tileTemplate: 'google:y',
    defaultEnabled: true
  },
  {
    id: 'google-img-pure',
    label: '谷歌影像(无注记)',
    role: 'basemap',
    type: 'raster',
    provider: 'google',
    datum: 'GCJ02',
    styleMode: 'satellite',
    needsKey: null,
    tileTemplate: 'google:s',
    defaultEnabled: true
  },
  {
    id: 'google-ter',
    label: '谷歌地形',
    role: 'basemap',
    type: 'raster',
    provider: 'google',
    datum: 'GCJ02',
    styleMode: 'outdoors',
    needsKey: null,
    tileTemplate: 'google:p',
    defaultEnabled: true
  },
  {
    // Esri 全球影像通常比免费谷歌 vt 瓦片更新，适合对比新建道路/桥梁
    id: 'esri-img',
    label: 'Esri高清影像',
    role: 'basemap',
    type: 'raster',
    provider: 'esri',
    styleMode: 'satellite',
    needsKey: null,
    tileTemplate: 'esri:world-imagery',
    defaultEnabled: true
  },
  {
    id: 'mapbox-outdoors',
    label: '骑行地图',
    role: 'basemap',
    type: 'mapbox-style',
    provider: 'mapbox',
    styleMode: 'outdoors',
    styleUrl: 'mapbox://styles/mapbox/outdoors-v12',
    defaultEnabled: true
  },
  {
    id: 'mapbox-outdoors-alt',
    label: '户外地图',
    role: 'basemap',
    type: 'mapbox-style',
    provider: 'mapbox',
    styleMode: 'terrain',
    styleUrl: 'mapbox://styles/mapbox/outdoors-v12',
    defaultEnabled: true
  },
  {
    id: 'mapbox-streets',
    label: '景观地图',
    role: 'basemap',
    type: 'mapbox-style',
    provider: 'mapbox',
    styleMode: 'streets',
    styleUrl: 'mapbox://styles/mapbox/streets-zh-v1',
    defaultEnabled: true
  },
  {
    id: 'mapbox-satellite-pure',
    label: 'Mapbox卫星',
    role: 'basemap',
    type: 'mapbox-style',
    provider: 'mapbox',
    styleMode: 'satellite',
    styleUrl: 'mapbox://styles/mapbox/satellite-v9',
    defaultEnabled: true
  },
  {
    id: 'mapbox-satellite',
    label: 'Mapbox卫星注记',
    role: 'basemap',
    type: 'mapbox-style',
    provider: 'mapbox',
    styleMode: 'hybrid',
    styleUrl: 'mapbox://styles/mapbox/satellite-streets-v12',
    defaultEnabled: true
  },

  // —— 叠加注记（多选）——
  {
    id: 'overlay-tianditu-cva',
    label: '天地标注',
    role: 'overlay',
    type: 'raster',
    provider: 'tianditu',
    needsKey: 'tianditu',
    tileTemplate: 'tianditu:cva_w',
    defaultEnabled: true
  },
  {
    id: 'overlay-tianditu-ibo',
    label: '国界线',
    role: 'overlay',
    type: 'raster',
    provider: 'tianditu',
    // 天地图 _w 瓦片：CGCS2000≈WGS84 / Web Mercator
    datum: 'WGS84',
    needsKey: 'tianditu',
    tileTemplate: 'tianditu:ibo_w',
    defaultEnabled: true,
    // 必显：每次切底图自动加载，图层面板置灰不可关闭
    mandatory: true,
    // 随底图坐标系自动纠偏（GCJ 底图下逐像素重采样对齐，见 gcjRasterTiles）
    autoAlign: true
  },
  {
    id: 'overlay-gaode-label',
    label: '高德标注',
    role: 'overlay',
    type: 'raster',
    provider: 'gaode',
    datum: 'GCJ02',
    needsKey: null,
    tileTemplate: 'gaode:label',
    defaultEnabled: true
  },
  {
    id: 'overlay-google-label',
    label: '谷歌标注',
    role: 'overlay',
    type: 'raster',
    provider: 'google',
    datum: 'GCJ02',
    needsKey: null,
    tileTemplate: 'google:h',
    defaultEnabled: true
  },
  {
    id: 'overlay-flight-routes',
    label: '飞行航线',
    role: 'overlay',
    type: 'placeholder',
    defaultEnabled: false
  }
];

function decorateBasemapFamilies() {
  MAP_PROVIDER_CATALOG.forEach((item) => {
    if (item.role !== 'basemap') return;
    const family = item.family
      || (item.provider && item.provider !== 'custom' ? item.provider : item.id);
    item.family = family;
    item.familyLabel = item.familyLabel
      || BASEMAP_FAMILY_META[family]?.label
      || item.label;
  });
}
decorateBasemapFamilies();
registerBasemapDatums(MAP_PROVIDER_CATALOG);

/** 单兵轨迹在指挥页显示的天数：0–30，非法值回退默认 */
export function clampAppTrackDisplayDays(raw, fallback = 7) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(30, Math.max(0, n));
}

export const DEFAULT_MAP_PROVIDER_SETTINGS = {
  // 默认 Mapbox 卫星：走代理时国内高德/天地图反而容易空白
  defaultBasemapId: 'mapbox-satellite-pure',
  defaultStyleMode: 'satellite',
  keys: {
    tianditu: '',
    amap: '',
    google: ''
  },
  /** id -> boolean，未列出则用目录 defaultEnabled */
  enabled: {},
  /** 自定义瓦片模板，需含 {z}{x}{y}，可用 {s} 子域 */
  customTiles: {
    'xingtu-img': '',
    'changguang-img': '',
    'jilin-img': ''
  },
  /** 自定义影像的坐标系：'' = 默认（WGS84，与改造前一致）/ 'WGS84' / 'GCJ02' */
  customTileDatums: {
    'xingtu-img': '',
    'changguang-img': '',
    'jilin-img': ''
  },
  /** 单兵 App 地图默认（暗色/卫星/街道/地形 + 叠加层） */
  appMap: {
    defaultBasemap: 'dark',
    overlays: { contour: false, roads: false, places: false },
    allowUserSwitch: true,
    /** 指挥页自动显示单兵 App 最近几天的上报坐标；0 表示不上图（仍写入轨迹库） */
    trackDisplayDays: 7
  }
};

export function normalizeMapProviderSettings(raw) {
  const base = {
    ...DEFAULT_MAP_PROVIDER_SETTINGS,
    keys: { ...DEFAULT_MAP_PROVIDER_SETTINGS.keys },
    enabled: { ...DEFAULT_MAP_PROVIDER_SETTINGS.enabled },
    customTiles: { ...DEFAULT_MAP_PROVIDER_SETTINGS.customTiles },
    customTileDatums: { ...DEFAULT_MAP_PROVIDER_SETTINGS.customTileDatums },
    appMap: {
      ...DEFAULT_MAP_PROVIDER_SETTINGS.appMap,
      overlays: { ...DEFAULT_MAP_PROVIDER_SETTINGS.appMap.overlays }
    }
  };
  if (!raw || typeof raw !== 'object') return base;

  const keys = { ...base.keys, ...(raw.keys || {}) };
  Object.keys(keys).forEach((k) => {
    keys[k] = String(keys[k] || '').trim();
  });

  const enabled = { ...base.enabled, ...(raw.enabled || {}) };
  const customTiles = { ...base.customTiles, ...(raw.customTiles || {}) };
  Object.keys(customTiles).forEach((k) => {
    customTiles[k] = String(customTiles[k] || '').trim();
  });
  const customTileDatums = normalizeCustomTileDatums(raw.customTileDatums);

  let defaultBasemapId = String(raw.defaultBasemapId || base.defaultBasemapId);
  if (defaultBasemapId === 'mapbox-satellite') {
    // 旧默认「Mapbox卫星」现拆成纯影像 / 混合，刷新默认跟卫星按钮走纯影像
    defaultBasemapId = 'mapbox-satellite-pure';
  }
  if (!MAP_PROVIDER_CATALOG.some((p) => p.id === defaultBasemapId && p.role === 'basemap')) {
    defaultBasemapId = base.defaultBasemapId;
  }

  const allowedModes = ['streets', 'satellite', 'hybrid', 'outdoors', 'terrain'];
  let defaultStyleMode = String(raw.defaultStyleMode || '').trim();
  if (!allowedModes.includes(defaultStyleMode)) {
    defaultStyleMode = getStyleModeFromBasemapId(defaultBasemapId) || base.defaultStyleMode;
  }

  const rawApp = raw.appMap && typeof raw.appMap === 'object' ? raw.appMap : {};
  const allowedAppBasemaps = ['dark', 'satellite', 'street', 'terrain'];
  let appBasemap = String(rawApp.defaultBasemap || '').trim();
  if (!allowedAppBasemaps.includes(appBasemap)) {
    // 未单独配置 App 时，跟随指挥页默认模式推导
    appBasemap = ({
      streets: 'street',
      satellite: 'satellite',
      hybrid: 'satellite',
      outdoors: 'terrain',
      terrain: 'terrain'
    })[defaultStyleMode] || base.appMap.defaultBasemap;
  }
  const rawOv = rawApp.overlays && typeof rawApp.overlays === 'object' ? rawApp.overlays : {};
  const appMap = {
    defaultBasemap: appBasemap,
    overlays: {
      contour: rawOv.contour === true,
      roads: rawOv.roads === true || defaultStyleMode === 'hybrid',
      places: rawOv.places === true
    },
    allowUserSwitch: rawApp.allowUserSwitch !== false,
    trackDisplayDays: clampAppTrackDisplayDays(rawApp.trackDisplayDays, base.appMap.trackDisplayDays)
  };

  return { defaultBasemapId, defaultStyleMode, keys, enabled, customTiles, customTileDatums, appMap };
}

/** 自定义影像可声明的坐标系（'' 表示默认 WGS84） */
export const CUSTOM_TILE_DATUM_OPTIONS = ['', 'WGS84', 'GCJ02'];

function normalizeCustomTileDatums(raw) {
  const out = { ...DEFAULT_MAP_PROVIDER_SETTINGS.customTileDatums };
  const src = raw && typeof raw === 'object' ? raw : {};
  Object.keys(out).forEach((k) => {
    const v = String(src[k] || '').trim().toUpperCase();
    out[k] = CUSTOM_TILE_DATUM_OPTIONS.includes(v) ? v : '';
  });
  return out;
}

/** 自定义影像在设置里声明的坐标系；非自定义或未声明返回 null */
export function getCustomTileDatumOverride(item, settings) {
  if (!item || item.provider !== 'custom') return null;
  const s = normalizeMapProviderSettings(settings);
  return s.customTileDatums[item.id] || null;
}

/**
 * 把设置里的自定义影像坐标系登记进纠偏注册表（全部为默认时与改造前一致）。
 * 设置加载后、切底图前调用。
 */
export function applyCustomTileDatums(settings) {
  const s = normalizeMapProviderSettings(settings);
  registerBasemapDatums(
    MAP_PROVIDER_CATALOG
      .filter((p) => p.provider === 'custom')
      .map((p) => ({ id: p.id, provider: p.provider, datum: s.customTileDatums[p.id] || p.datum || null }))
  );
}

/** 目录项（底图或叠加层）的显示坐标系 */
export function getCatalogItemDatum(id, settings) {
  const item = MAP_PROVIDER_CATALOG.find((p) => p.id === id);
  if (!item) return resolveBasemapDatum(id);
  return getCustomTileDatumOverride(item, settings) || item.datum || resolveBasemapDatum(id, { provider: item.provider });
}

/**
 * 已勾选的栅格叠加层里，与底图坐标系不一致的项（会整体偏几百米，需提示）
 * @returns {Array<{ id:string, label:string, datum:string, basemapDatum:string }>}
 */
export function findOverlayDatumMismatches(basemapId, overlayIds, settings) {
  const basemapDatum = getCatalogItemDatum(basemapId, settings);
  return (overlayIds || [])
    .map((oid) => MAP_PROVIDER_CATALOG.find((p) => p.id === oid))
    // autoAlign 叠加层（国界线）会按底图自动纠偏，不算不一致
    .filter((item) => item && item.role === 'overlay' && item.type === 'raster' && !item.autoAlign)
    .map((item) => ({ id: item.id, label: item.label, datum: getCatalogItemDatum(item.id, settings), basemapDatum }))
    .filter((x) => x.datum !== basemapDatum);
}

export function resolveProviderKeys(settings) {
  const s = normalizeMapProviderSettings(settings);
  return {
    tianditu: s.keys.tianditu || getRuntimeKey('TIANDITU_KEY'),
    amap: s.keys.amap || getRuntimeKey('AMAP_KEY'),
    google: s.keys.google || getRuntimeKey('GOOGLE_MAPS_KEY'),
    mapbox: getMapboxToken()
  };
}

export function isProviderEnabled(settings, providerId) {
  const s = normalizeMapProviderSettings(settings);
  const item = MAP_PROVIDER_CATALOG.find((p) => p.id === providerId);
  if (!item) return false;
  if (Object.prototype.hasOwnProperty.call(s.enabled, providerId)) {
    return !!s.enabled[providerId];
  }
  return item.defaultEnabled !== false;
}

export function getEnabledProviders(settings, role) {
  return MAP_PROVIDER_CATALOG.filter(
    (p) => (!role || p.role === role) && isProviderEnabled(settings, p.id)
  );
}

function expandSubdomains(urlTemplate, subs) {
  return subs.map((s) => urlTemplate.replace(/\{s\}/g, s));
}

/** 根据模板生成 Mapbox raster tiles 数组 */
export function buildTileUrls(tileTemplate, keys, customTiles = {}) {
  const tk = keys.tianditu || '';

  if (tileTemplate.startsWith('tianditu:')) {
    const layer = tileTemplate.split(':')[1];
    if (!tk) return [];
    const tpl = `https://t{s}.tianditu.gov.cn/DataServer?T=${layer}&x={x}&y={y}&l={z}&tk=${encodeURIComponent(tk)}`;
    return expandSubdomains(tpl, ['0', '1', '2', '3', '4', '5', '6', '7']);
  }

  if (tileTemplate === 'gaode:vec') {
    return expandSubdomains(
      'https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}',
      ['1', '2', '3', '4']
    );
  }
  if (tileTemplate === 'gaode:img') {
    return expandSubdomains(
      'https://webst0{s}.is.autonavi.com/appmaptile?style=6&x={x}&y={y}&z={z}',
      ['1', '2', '3', '4']
    );
  }
  if (tileTemplate === 'gaode:label') {
    return expandSubdomains(
      'https://webst0{s}.is.autonavi.com/appmaptile?style=8&x={x}&y={y}&z={z}',
      ['1', '2', '3', '4']
    );
  }
  if (tileTemplate === 'gaode:ter') {
    // 高德无公开地形服务：使用深色矢量样式，作为族内「高程」快捷切换目标
    return expandSubdomains(
      'https://wprd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&style=7&x={x}&y={y}&z={z}&scl=1&ltype=11',
      ['1', '2', '3', '4']
    );
  }

  if (tileTemplate === 'google:m') {
    return expandSubdomains(
      'https://mt{s}.google.com/vt/lyrs=m&hl=zh-CN&gl=CN&scale=2&x={x}&y={y}&z={z}',
      ['0', '1', '2', '3']
    );
  }
  if (tileTemplate === 'google:s') {
    return expandSubdomains(
      'https://mt{s}.google.com/vt/lyrs=s&hl=zh-CN&gl=CN&scale=2&x={x}&y={y}&z={z}',
      ['0', '1', '2', '3']
    );
  }
  // 混合：卫星 + 谷歌中文标注（图上常有 © Google 水印，便于确认已切到谷歌）
  if (tileTemplate === 'google:y') {
    return expandSubdomains(
      'https://mt{s}.google.com/vt/lyrs=y&hl=zh-CN&gl=CN&scale=2&x={x}&y={y}&z={z}',
      ['0', '1', '2', '3']
    );
  }
  if (tileTemplate === 'google:p') {
    return expandSubdomains(
      'https://mt{s}.google.com/vt/lyrs=p&hl=zh-CN&gl=CN&scale=2&x={x}&y={y}&z={z}',
      ['0', '1', '2', '3']
    );
  }
  if (tileTemplate === 'google:h') {
    return expandSubdomains(
      'https://mt{s}.google.com/vt/lyrs=h&hl=zh-CN&gl=CN&scale=2&x={x}&y={y}&z={z}',
      ['0', '1', '2', '3']
    );
  }

  // Esri World Imagery（XYZ 为 z/y/x）
  if (tileTemplate === 'esri:world-imagery') {
    return [
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    ];
  }

  if (tileTemplate.startsWith('custom:')) {
    const key = tileTemplate.slice('custom:'.length);
    const custom = String(customTiles[key] || '').trim();
    if (!custom || !custom.includes('{z}') || !custom.includes('{x}') || !custom.includes('{y}')) {
      return [];
    }
    if (custom.includes('{s}')) {
      return expandSubdomains(custom, ['0', '1', '2', '3']);
    }
    return [custom];
  }

  return [];
}

function catalogItemById(id) {
  return MAP_PROVIDER_CATALOG.find((p) => p.id === id) || null;
}

/** 必显叠加层（国界线）：不可关闭、每次构建底图样式都自动带上 */
export const MANDATORY_OVERLAY_IDS = MAP_PROVIDER_CATALOG
  .filter((p) => p.role === 'overlay' && p.mandatory)
  .map((p) => p.id);

export function isMandatoryOverlay(id) {
  return MANDATORY_OVERLAY_IDS.includes(id);
}

/**
 * 在叠加层列表末尾补上必显叠加层（已在列表中的保持原位置）。
 * 管理后台未启用或缺 Key（瓦片地址为空）时无法加载，不补。
 */
export function withMandatoryOverlays(overlayIds, settings) {
  const next = Array.isArray(overlayIds) ? [...overlayIds] : [];
  MANDATORY_OVERLAY_IDS.forEach((id) => {
    if (!next.includes(id) && isProviderEnabled(settings, id) && canUseProvider(id, settings)) {
      next.push(id);
    }
  });
  return next;
}

/** 叠加层瓦片：声明 autoAlign 的按底图坐标系纠偏；其余原样（不改变其他叠加层行为） */
function resolveOverlayTiles(item, tiles, basemapId, settings) {
  if (!item.autoAlign || !basemapId) return tiles;
  return resolveRasterTilesForBasemap(basemapId, tiles, {
    sourceDatum: getCatalogItemDatum(item.id, settings)
  }).tiles;
}

/**
 * 构建纯栅格底图 + 叠加层的 Mapbox Style
 * @param {{ globe?: boolean }} styleOptions globe=false 时用墨卡托（指挥视野），避免刷新白球
 */
export function buildRasterMapStyle(basemapId, overlayIds, settings, styleOptions = {}) {
  const s = normalizeMapProviderSettings(settings);
  const keys = resolveProviderKeys(s);
  const basemap = catalogItemById(basemapId);
  const useGlobe = styleOptions.globe !== false;
  const sources = {};
  const layers = [
    {
      id: 'background',
      type: 'background',
      // 地球态：深空黑；普通指挥视野：浅底，避免整页像坏掉的白球
      paint: { 'background-color': useGlobe ? EARTH_SPACE_BG : '#d9e2ec' }
    }
  ];

  if (basemap && basemap.type === 'raster') {
    const tiles = buildTileUrls(basemap.tileTemplate, keys, s.customTiles);
    if (tiles.length > 0) {
      sources['basemap-raster'] = {
        type: 'raster',
        tiles,
        tileSize: 256,
        attribution: basemap.label
      };
      layers.push({
        id: 'basemap-raster-layer',
        type: 'raster',
        source: 'basemap-raster',
        minzoom: 0,
        maxzoom: 22
      });
    }
  }

  withMandatoryOverlays(overlayIds, s).forEach((oid) => {
    const item = catalogItemById(oid);
    if (!item || item.role !== 'overlay' || item.type !== 'raster') return;
    const rawTiles = buildTileUrls(item.tileTemplate, keys, s.customTiles);
    if (!rawTiles.length) return;
    const tiles = resolveOverlayTiles(item, rawTiles, basemapId, s);
    const sid = `overlay-${item.id}`;
    sources[sid] = {
      type: 'raster',
      tiles,
      tileSize: 256
    };
    layers.push({
      id: `${sid}-layer`,
      type: 'raster',
      source: sid,
      minzoom: 0,
      maxzoom: 22,
      paint: { 'raster-opacity': 1 }
    });
  });

  return {
    version: 8,
    name: `zhxt-${basemapId}`,
    sources,
    layers,
    // 仅地球全景用球体；指挥视野用墨卡托，刷新不会卡在不转的白球
    projection: { name: useGlobe ? 'globe' : 'mercator' },
    // 不在 style JSON 内嵌 fog：setStyle 过渡期 fog.state 未就绪时 jumpTo 会崩溃
    // 深空 fog 改在 idle 后由 applyEarthDeepSpaceLook 施加
    // 标注文字偶尔需要 glyphs；无矢量标注时可不依赖 Mapbox
    glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf'
  };
}

/**
 * 在已有 Mapbox 样式上叠加 raster 注记层
 */
export function applyOverlayLayers(map, overlayIds, settings, basemapId = null) {
  if (!map) return;
  const displayBasemapId = basemapId || getActiveBasemapIdForCoord(map);
  const s = normalizeMapProviderSettings(settings);
  const keys = resolveProviderKeys(s);

  // 清理旧叠加层
  MAP_PROVIDER_CATALOG.filter((p) => p.role === 'overlay').forEach((item) => {
    const layerId = `overlay-${item.id}-layer`;
    const sourceId = `overlay-${item.id}`;
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    } catch (_) {
      /* ignore */
    }
  });

  withMandatoryOverlays(overlayIds, s).forEach((oid) => {
    const item = catalogItemById(oid);
    if (!item || item.role !== 'overlay' || item.type !== 'raster') return;
    const rawTiles = buildTileUrls(item.tileTemplate, keys, s.customTiles);
    if (!rawTiles.length) return;
    const tiles = resolveOverlayTiles(item, rawTiles, displayBasemapId, s);
    const sourceId = `overlay-${item.id}`;
    const layerId = `${sourceId}-layer`;
    try {
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: 'raster',
          tiles,
          tileSize: 256
        });
      }
      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: { 'raster-opacity': 1 }
        });
      }
    } catch (e) {
      console.warn('叠加注记层失败:', item.id, e);
    }
  });
}

/**
 * 补齐必显叠加层（国界线）：已存在则不动；缺 Key / 后台停用 / 样式未就绪时静默跳过。
 * 用于：指挥页地图每次 style.load（含首屏 Mapbox 官方样式，不经 applyBasemap 的情况）、
 * 以及异步拿到含 Key 的后台配置之后。
 * @returns {string[]} 本次新加的叠加层 id
 */
export function ensureMandatoryOverlays(map, settings, basemapId = null) {
  if (!map || typeof map.getSource !== 'function') return [];
  const s = normalizeMapProviderSettings(settings);
  const keys = resolveProviderKeys(s);
  const displayBasemapId = basemapId || getActiveBasemapIdForCoord(map);
  const added = [];
  withMandatoryOverlays([], s).forEach((oid) => {
    const item = catalogItemById(oid);
    if (!item || item.role !== 'overlay' || item.type !== 'raster') return;
    const sourceId = `overlay-${item.id}`;
    const layerId = `${sourceId}-layer`;
    try {
      if (map.getSource(sourceId) && map.getLayer(layerId)) return;
      const rawTiles = buildTileUrls(item.tileTemplate, keys, s.customTiles);
      if (!rawTiles.length) return;
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: 'raster',
          tiles: resolveOverlayTiles(item, rawTiles, displayBasemapId, s),
          tileSize: 256
        });
      }
      if (!map.getLayer(layerId)) {
        map.addLayer({ id: layerId, type: 'raster', source: sourceId, paint: { 'raster-opacity': 1 } });
      }
      added.push(item.id);
    } catch (_) {
      /* 样式尚未就绪：下一次 style.load 再补 */
    }
  });
  return added;
}

/**
 * 切换底图（Mapbox 样式或栅格 Style）
 * @param {object} [options]
 * @param {(basemapId: string) => void} [options.onSettled] 样式 idle 且相机恢复后回调（用于重绘标记纠偏）
 * @returns {'mapbox'|'raster'|null}
 */
export function applyBasemap(map, basemapId, overlayIds, settings, options = {}) {
  if (!map) return null;
  const s = normalizeMapProviderSettings(settings);
  const item = catalogItemById(basemapId);
  if (!item || item.role !== 'basemap') return null;

  // 旧底图 id：优先用调用方传入（切图前快照），避免已被提前改成新 id 导致跳过纠偏飞行
  const prevBasemapId = options.prevBasemapId || getActiveBasemapIdForCoord(map);
  const prevDatum = getBasemapDatum(prevBasemapId);
  // 自定义影像坐标系可在后台声明：先登记再判定新基准（全部默认时与改造前一致）
  applyCustomTileDatums(s);
  const nextDatum = getBasemapDatum(basemapId);
  const datumChanged = prevDatum !== nextDatum;
  // 在判定完 datumChanged 之后再写入新基准
  setActiveBasemapIdForCoord(basemapId, map, {
    provider: item.provider,
    datum: getCustomTileDatumOverride(item, s) || item.datum
  });

  // 切样式前记下「当前显示坐标」；跨 WGS84/GCJ02 时转到新基准
  let keepLng = null;
  let keepLat = null;
  let keepZoom = null;
  try {
    const c = map.getCenter?.();
    keepLng = c?.lng;
    keepLat = c?.lat;
    keepZoom = map.getZoom?.();
  } catch (_) {
    /* ignore */
  }
  if (
    keepLng != null
    && keepLat != null
    && datumChanged
  ) {
    const converted = convertDisplayLngLat(keepLng, keepLat, prevDatum, nextDatum);
    if (converted) {
      keepLng = converted[0];
      keepLat = converted[1];
    }
  }

  // 跨基准时：setStyle 前先把 HTML 标记纠到新坐标系，并 jumpTo 新中心。
  // Esri/Mapbox/天地图均为 WGS84，与高德/谷歌之间只做「一次」基准换算；
  // 禁止对 Esri 再做国测局纠偏（否则会先东南偏再跳回）。
  if (datumChanged) {
    if (isDashboardEarthDismissed()) {
      try {
        const liveZ = map.getZoom?.();
        const liveC = map.getCenter?.();
        if (Number.isFinite(Number(liveZ)) && Number(liveZ) > 2.5 && liveC) {
          keepZoom = liveZ;
          keepLng = liveC.lng;
          keepLat = liveC.lat;
        } else if (!Number.isFinite(Number(keepZoom)) || Number(keepZoom) <= 2.5) {
          keepZoom = 10;
          keepLng = 119.13643;
          keepLat = 28.07741;
        }
      } catch (_) {
        /* ignore */
      }
    }
    try {
      options.onSettled?.(basemapId, {
        datumChanged,
        phase: 'before-style',
        center: keepLng != null && keepLat != null ? [keepLng, keepLat] : null,
        zoom: keepZoom
      });
    } catch (e) {
      console.warn('切图前重投影覆盖物失败:', e);
    }
    try {
      // 用户已离开地球且正在 flyTo：不要 stop 打断飞行
      if (isDashboardEarthDismissed()) {
        const liveZ = map.getZoom?.();
        if (Number.isFinite(Number(liveZ)) && Number(liveZ) > 2.5) {
          /* skip pre-style jump */
        } else if (keepLng != null && Number.isFinite(Number(keepLng))) {
          const z = Number.isFinite(Number(keepZoom)) ? keepZoom : 10;
          const lat = Number.isFinite(Number(keepLat)) ? keepLat : 28.07741;
          map.jumpTo(
            { center: [Number(keepLng), Number(lat)], zoom: z },
            { zhxtEarthSpin: true }
          );
        }
      } else if (keepLng != null && Number.isFinite(Number(keepLng))) {
        const z = Number.isFinite(Number(keepZoom)) ? keepZoom : map.getZoom?.();
        const lat = Number.isFinite(Number(keepLat)) ? keepLat : 20;
        map.stop?.();
        map.jumpTo(
          { center: [Number(keepLng), Number(lat)], zoom: z },
          { zhxtEarthSpin: true }
        );
      }
    } catch (_) {
      /* ignore */
    }
  }

  // 地球全景（低缩放）才用球体；用户已离开地球后绝不再用 globe / zoom≈1 拽回
  let preferGlobe = !isDashboardEarthDismissed() && (
    options.globe === true
    || (options.globe !== false && Number.isFinite(Number(keepZoom)) && Number(keepZoom) <= 2.5)
  );

  const COMMAND_FALLBACK = { lng: 119.13643, lat: 28.07741, zoom: 10 };

  /** 用户已飞离地球时，禁止把相机恢复成全景 */
  const syncKeepCameraAfterEarthExit = () => {
    if (!isDashboardEarthDismissed()) return;
    preferGlobe = false;
    try {
      const liveZ = map.getZoom?.();
      const liveC = map.getCenter?.();
      if (Number.isFinite(Number(liveZ)) && Number(liveZ) > 2.5 && liveC) {
        keepZoom = liveZ;
        keepLng = liveC.lng;
        keepLat = liveC.lat;
        return;
      }
    } catch (_) {
      /* ignore */
    }
    if (!Number.isFinite(Number(keepZoom)) || Number(keepZoom) <= 2.5) {
      keepZoom = COMMAND_FALLBACK.zoom;
      keepLng = COMMAND_FALLBACK.lng;
      keepLat = COMMAND_FALLBACK.lat;
    }
  };
  syncKeepCameraAfterEarthExit();

  /** 用已换算好的 keepLng/keepLat 对齐相机（不再读 getCenter 二次纠偏） */
  const restoreCameraSoft = ({ animate = false } = {}) => {
    // 切图完成瞬间再读一次：用户可能已在 flyTo 途中
    syncKeepCameraAfterEarthExit();
    if (isDashboardEarthDismissed()) {
      try {
        // 地球俯冲 flyTo 进行中：绝对不要改投影/雾效/相机，否则会「拍扁」
        if (map.isMoving?.()) return;
        const liveZ = map.getZoom?.();
        if (Number.isFinite(Number(liveZ)) && Number(liveZ) > 2.5) {
          // 已在地图侧：不打断、不强制 mercator（由 schedulePostEarthDiveCleanup 收尾）
          return;
        }
      } catch (_) {
        /* ignore */
      }
    }
    try {
      if (typeof map.setProjection === 'function') {
        const proj = map.getProjection?.();
        const name = typeof proj === 'string' ? proj : proj?.name;
        const want = preferGlobe ? 'globe' : 'mercator';
        if (name !== want) map.setProjection(want);
      }
    } catch (_) {
      /* ignore */
    }
    if (keepLng == null || !Number.isFinite(Number(keepLng))) return;
    try {
      const zoom = Number.isFinite(Number(keepZoom)) ? keepZoom : map.getZoom?.();
      const lat = Number.isFinite(Number(keepLat)) ? keepLat : map.getCenter?.()?.lat;
      const center = [Number(keepLng), Number(lat ?? 20)];
      const z = Number(zoom ?? map.getZoom?.() ?? 14);
      // 仅切到高德/谷歌（GCJ）时平滑飞行；切到 Esri/Mapbox/天地图只静默 jumpTo
      const shouldFly = animate && nextDatum === 'GCJ02' && z > 2.5;
      try {
        map.stop?.();
      } catch (_) {
        /* ignore */
      }
      // 离开地球后禁止 stop+jump 打断正在进行的指挥视野 flyTo（上面已 early-return；此处兜底）
      if (isDashboardEarthDismissed() && z > 2.5) {
        return;
      }
      if (shouldFly) {
        map.flyTo({
          center,
          zoom: z,
          duration: 1400,
          essential: true,
          easing: (t) => 1 - Math.pow(1 - t, 3)
        });
      } else {
        map.jumpTo(
          { center, zoom: z },
          { zhxtEarthSpin: true }
        );
      }
    } catch (_) {
      /* ignore */
    }
  };

  const finishStyleSwitch = (afterLoad) => {
    // 官方 Mapbox 样式自带 fog；先清掉，等 idle 再统一施加，避免过渡期崩溃
    try {
      if (typeof map.setFog === 'function') map.setFog(null);
    } catch (_) {
      /* ignore */
    }
    try {
      applyEarthSpaceBackground(map);
    } catch (_) {
      /* ignore */
    }
    // setStyle 常会把相机短暂打回切图前中心；跨基准时在 style.load 立刻用已换算坐标顶回去，
    // 不要等 idle（否则 Esri 上会先看到东南偏，idle 后再跳回）
    if (datumChanged) {
      try {
        restoreCameraSoft({ animate: false });
      } catch (_) {
        /* ignore */
      }
      try {
        options.onSettled?.(basemapId, {
          datumChanged,
          phase: 'style-load',
          nextDatum,
          center: keepLng != null && keepLat != null ? [keepLng, keepLat] : null,
          zoom: keepZoom
        });
      } catch (e) {
        console.warn('style.load 重投影覆盖物失败:', e);
      }
    }
    try {
      afterLoad?.();
    } catch (e) {
      console.warn('底图切换后处理失败:', e);
    }

    // 必须等 idle：户外/景观等矢量样式 fog 内部 state 未就绪时 jumpTo 会炸
    whenMapStyleSettled(map, () => {
      try {
        if (preferGlobe) {
          applyEarthDeepSpaceLook(map, { allowDuringPause: true });
        } else if (typeof map.setFog === 'function') {
          map.setFog(null);
        }
      } catch (_) {
        /* ignore */
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          pauseEarthSpinRender(false);
          try {
            options.onSettled?.(basemapId, {
              datumChanged,
              phase: 'after-style',
              nextDatum,
              center: keepLng != null && keepLat != null ? [keepLng, keepLat] : null,
              zoom: keepZoom
            });
          } catch (e) {
            console.warn('底图切换后重绘失败:', e);
          }
          try {
            restoreMapTerrain3d(map, { skipPitch: true });
          } catch (_) {
            /* ignore */
          }
          try {
            // 同基准（Mapbox↔Esri）不动相机；仅切到 GCJ 才 fly；切回 WGS84 最多再 jump 一次对齐
            if (datumChanged && nextDatum === 'GCJ02') {
              restoreCameraSoft({ animate: true });
            } else if (datumChanged && nextDatum === 'WGS84') {
              restoreCameraSoft({ animate: false });
            }
          } catch (_) {
            /* ignore */
          }
        });
      });
    });
  };

  // 过渡期：停自转渲染 + 清 fog/terrain，防止 getOpacityAtLatLng 读到 undefined.state
  pauseEarthSpinRender(true);
  const safetyUnpause = setTimeout(() => pauseEarthSpinRender(false), 8000);
  try {
    if (typeof map.setFog === 'function') map.setFog(null);
  } catch (_) {
    /* ignore */
  }
  try {
    if (typeof map.setTerrain === 'function') map.setTerrain(null);
  } catch (_) {
    /* ignore */
  }

  // 注意：once('style.load') 必须在 setStyle 之前注册，避免同步加载漏事件
  if (item.type === 'mapbox-style') {
    map.once('style.load', () => {
      clearTimeout(safetyUnpause);
      finishStyleSwitch(() => applyOverlayLayers(map, overlayIds, s, basemapId));
    });
    map.setStyle(item.styleUrl, { diff: false });
    return 'mapbox';
  }

  if (item.type === 'raster') {
    const style = buildRasterMapStyle(basemapId, overlayIds, s, { globe: preferGlobe });
    const hasBasemap = !!style.sources['basemap-raster'];
    if (!hasBasemap) {
      console.warn('底图瓦片不可用（可能缺少 Key 或自定义地址）:', basemapId);
    }
    map.once('style.load', () => {
      clearTimeout(safetyUnpause);
      finishStyleSwitch();
    });
    map.setStyle(style, { diff: false });
    return 'raster';
  }

  clearTimeout(safetyUnpause);
  pauseEarthSpinRender(false);
  return null;
}

/**
 * 指挥页启动/刷新时使用的底图：优先管理后台默认底图，
 * 若未启用或不可用则回退到第一个可用底图。
 * （不再用 localStorage 记住上次选择，避免无 VPN 时卡在谷歌等外网图源）
 */
export function resolveStartupBasemapId(settings) {
  const s = normalizeMapProviderSettings(settings);
  const seed = s.defaultBasemapId;
  const preferredMode = s.defaultStyleMode || getStyleModeFromBasemapId(seed);
  const byMode = resolveBasemapIdForStyleMode(seed, preferredMode, s);
  if (byMode.basemapId) return byMode.basemapId;

  const fallbackOrder = ['streets', 'satellite', 'hybrid', 'outdoors', 'terrain'];
  for (let i = 0; i < fallbackOrder.length; i += 1) {
    const resolved = resolveBasemapIdForStyleMode(seed, fallbackOrder[i], s);
    if (resolved.basemapId) return resolved.basemapId;
  }

  const enabledUsable = getEnabledProviders(s, 'basemap').find((p) => canUseProvider(p.id, s));
  if (enabledUsable) return enabledUsable.id;
  const anyUsable = MAP_PROVIDER_CATALOG.find(
    (p) => p.role === 'basemap' && canUseProvider(p.id, s)
  );
  return anyUsable?.id || seed || DEFAULT_MAP_PROVIDER_SETTINGS.defaultBasemapId;
}

export function resolveStartupStyleMode(settings, basemapId) {
  const s = normalizeMapProviderSettings(settings);
  const id = basemapId || s.defaultBasemapId;
  const inferred = getStyleModeFromBasemapId(id);
  const pref = s.defaultStyleMode;
  if (pref === 'hybrid' && (inferred === 'hybrid' || inferred === 'satellite')) return 'hybrid';
  if (pref && resolveBasemapIdForStyleMode(id, pref, s).basemapId) return pref;
  return inferred;
}

/** @deprecated 启动已改为 resolveStartupBasemapId；保留供兼容旧调用 */
export function readLastBasemapChoice(fallbackId) {
  return fallbackId || DEFAULT_MAP_PROVIDER_SETTINGS.defaultBasemapId;
}

/** 会话内切换仍可写入，但刷新后不再读取 */
export function writeLastBasemapChoice(id) {
  try {
    localStorage.setItem(STORAGE_LAST_BASEMAP, id);
  } catch (_) {
    /* ignore */
  }
}

export function readLastOverlayChoice() {
  try {
    const raw = localStorage.getItem(STORAGE_LAST_OVERLAYS);
    const list = raw ? JSON.parse(raw) : [];
    // 必显叠加层（国界线）忽略本地存的「已关闭」
    const ids = Array.isArray(list) ? list : [];
    return [...ids, ...MANDATORY_OVERLAY_IDS.filter((id) => !ids.includes(id))];
  } catch (_) {
    return [...MANDATORY_OVERLAY_IDS];
  }
}

export function writeLastOverlayChoice(ids) {
  try {
    localStorage.setItem(STORAGE_LAST_OVERLAYS, JSON.stringify(ids || []));
  } catch (_) {
    /* ignore */
  }
}

/** 检查某底图当前是否可切换（Key/自定义地址齐全） */
export function canUseProvider(providerId, settings) {
  const item = catalogItemById(providerId);
  if (!item) return false;
  if (item.type === 'placeholder') return false;
  if (item.type === 'mapbox-style') return !!getMapboxToken();
  if (item.type === 'raster') {
    const s = normalizeMapProviderSettings(settings);
    const keys = resolveProviderKeys(s);
    return buildTileUrls(item.tileTemplate, keys, s.customTiles).length > 0;
  }
  return false;
}

export function getProviderHint(providerId, settings) {
  const item = catalogItemById(providerId);
  if (!item) return '';
  if (item.type === 'placeholder') return '功能预留，后续版本支持';
  if (item.type === 'mapbox-style' && !getMapboxToken()) return '需配置 Mapbox Token';
  if (item.needsKey === 'tianditu' && !resolveProviderKeys(settings).tianditu) {
    return '需在管理后台填写天地图 Key';
  }
  if (item.provider === 'google') {
    return '国内网络通常打不开谷歌瓦片（mt.google.com），地图会空白；能翻墙或可访问谷歌时才能用。影像也旧于谷歌地球。';
  }
  if (item.provider === 'esri') {
    return 'Esri 全球影像，更新频率通常高于免费谷歌瓦片，适合核对新建地物';
  }
  if (item.tileTemplate && String(item.tileTemplate).startsWith('custom:')) {
    const key = item.tileTemplate.slice('custom:'.length);
    const s = normalizeMapProviderSettings(settings);
    if (!s.customTiles[key]) return '需在管理后台填写自定义瓦片地址';
  }
  if (!canUseProvider(providerId, settings)) return '当前不可用';
  return '';
}

const SATELLITE_FALLBACK_IDS = [
  'esri-img',
  'tianditu-img',
  'gaode-img',
  'mapbox-satellite-pure'
];

/** 谷歌瓦片不可达时改用的卫星底图 */
export function pickReachableSatelliteBasemap(settings, excludeId) {
  const s = normalizeMapProviderSettings(settings);
  for (let i = 0; i < SATELLITE_FALLBACK_IDS.length; i += 1) {
    const id = SATELLITE_FALLBACK_IDS[i];
    if (id === excludeId) continue;
    if (!isProviderEnabled(s, id)) continue;
    if (!canUseProvider(id, s)) continue;
    return id;
  }
  return null;
}

/**
 * 监听栅格底图瓦片错误。谷歌在国内常全部失败，确认后回调以便改用其它卫星图。
 * @returns {() => void} 取消监听
 */
export function attachUnreachableRasterFallback(map, { onFallback } = {}) {
  if (!map || typeof map.on !== 'function') return () => {};
  let settled = false;
  let errorCount = 0;
  let timer = null;
  const finish = (failed) => {
    if (settled) return;
    settled = true;
    if (timer) clearTimeout(timer);
    try { map.off('error', onError); } catch (_) { /* ignore */ }
    if (failed) onFallback?.();
  };
  const onError = (e) => {
    const sourceId = e?.sourceId || '';
    const hasTile = !!e?.tile;
    if (sourceId !== 'basemap-raster' && !(hasTile && !sourceId)) return;
    errorCount += 1;
    if (errorCount >= 2) finish(true);
  };
  try {
    map.on('error', onError);
  } catch (_) {
    return () => {};
  }
  timer = setTimeout(() => {
    if (settled) return;
    let tilesOk = false;
    try {
      tilesOk = typeof map.areTilesLoaded === 'function' && map.areTilesLoaded();
    } catch (_) {
      tilesOk = false;
    }
    finish(errorCount > 0 && !tilesOk);
  }, 6000);
  return () => finish(false);
}

/** 从底图 id 解析图商族 */
export function getBasemapFamily(basemapId) {
  const item = catalogItemById(basemapId);
  if (item?.family) return item.family;
  if (item?.provider && item.provider !== 'custom') return item.provider;
  if (item?.type === 'mapbox-style') return 'mapbox';
  const id = String(basemapId || '');
  if (id.startsWith('gaode')) return 'gaode';
  if (id.startsWith('tianditu')) return 'tianditu';
  if (id.startsWith('google')) return 'google';
  if (id.startsWith('mapbox')) return 'mapbox';
  if (id.startsWith('esri')) return 'esri';
  return item?.id || 'custom';
}

export function getBasemapFamilyLabel(familyOrBasemapId) {
  const family = BASEMAP_FAMILY_META[familyOrBasemapId]
    ? familyOrBasemapId
    : getBasemapFamily(familyOrBasemapId);
  const item = catalogItemById(familyOrBasemapId);
  return item?.familyLabel
    || BASEMAP_FAMILY_META[family]?.label
    || item?.label
    || family
    || '当前';
}

export function listBasemapFamilies() {
  const map = new Map();
  MAP_PROVIDER_CATALOG.filter((p) => p.role === 'basemap').forEach((p) => {
    const family = p.family || getBasemapFamily(p.id);
    if (!map.has(family)) {
      map.set(family, {
        family,
        label: p.familyLabel || BASEMAP_FAMILY_META[family]?.label || p.label,
        memberIds: []
      });
    }
    map.get(family).memberIds.push(p.id);
  });
  const modeRank = ['streets', 'satellite', 'hybrid', 'outdoors', 'terrain'];
  return [...map.values()].map((group) => {
    let seedId = group.memberIds[0];
    for (let i = 0; i < modeRank.length; i += 1) {
      const hit = MAP_PROVIDER_CATALOG.find(
        (p) => group.memberIds.includes(p.id) && p.styleMode === modeRank[i]
      );
      if (hit) {
        seedId = hit.id;
        break;
      }
    }
    return { ...group, seedId };
  });
}

export function isFamilyEnabled(settings, family) {
  const group = listBasemapFamilies().find((f) => f.family === family);
  if (!group) return false;
  return group.memberIds.some((id) => isProviderEnabled(settings, id));
}

export function setFamilyEnabled(enabledMap, family, on) {
  const group = listBasemapFamilies().find((f) => f.family === family);
  const next = { ...(enabledMap || {}) };
  (group?.memberIds || []).forEach((id) => {
    next[id] = Boolean(on);
  });
  return next;
}

export function getEnabledBasemapFamilies(settings) {
  return listBasemapFamilies().filter((group) => isFamilyEnabled(settings, group.family));
}

function familyModeCandidates(family, mode) {
  return MAP_PROVIDER_CATALOG.filter(
    (p) => p.role === 'basemap' && getBasemapFamily(p.id) === family && p.styleMode === mode
  );
}

function pickUsableCandidate(candidates, settings) {
  const usable = candidates.find(
    (p) => isProviderEnabled(settings, p.id) && canUseProvider(p.id, settings)
  );
  if (usable) return { ok: true, item: usable };
  const anyEnabled = candidates.find((p) => isProviderEnabled(settings, p.id));
  if (anyEnabled) {
    return {
      ok: false,
      item: anyEnabled,
      hint: getProviderHint(anyEnabled.id, settings) || '当前样式不可用'
    };
  }
  return { ok: false, item: null, hint: '' };
}

function allRoadOverlayIds() {
  return Object.values(BASEMAP_FAMILY_META)
    .map((m) => m.roadOverlayId)
    .filter(Boolean);
}

/**
 * 混合模式：优先用带路网的卫星样式；否则卫星底图 + 同族路网叠加 + 山体。
 */
export function resolveHybridPresentation(currentBasemapId, settings) {
  const family = getBasemapFamily(currentBasemapId);
  const hybridPick = pickUsableCandidate(familyModeCandidates(family, 'hybrid'), settings);
  if (hybridPick.ok) {
    return {
      basemapId: hybridPick.item.id,
      extraOverlayId: null,
      enableTerrain: true
    };
  }
  const satPick = pickUsableCandidate(familyModeCandidates(family, 'satellite'), settings);
  if (satPick.ok) {
    return {
      basemapId: satPick.item.id,
      extraOverlayId: BASEMAP_FAMILY_META[family]?.roadOverlayId || null,
      enableTerrain: true
    };
  }
  return {
    basemapId: null,
    extraOverlayId: null,
    enableTerrain: true,
    hint: satPick.hint || hybridPick.hint || `${getBasemapFamilyLabel(family)}暂不支持混合地图`
  };
}

/** 从底图 id 解析原生样式模式（不含「卫星底图当混合用」） */
export function getStyleModeFromBasemapId(basemapId) {
  const item = catalogItemById(basemapId);
  if (item?.styleMode) return item.styleMode;
  const id = String(basemapId || '');
  if (id.includes('img') || id.includes('satellite')) return 'satellite';
  if (id.includes('ter')) return 'terrain';
  if (id.includes('outdoors')) return 'outdoors';
  if (id.includes('vec') || id.includes('streets')) return 'streets';
  return 'satellite';
}

/**
 * 在当前图商族内，按样式模式解析目标底图 id
 * @returns {{ basemapId: string|null, hint?: string, extraOverlayId?: string|null, enableTerrain?: boolean }}
 */
export function resolveBasemapIdForStyleMode(currentBasemapId, styleMode, settings) {
  const mode = ALL_BASEMAP_STYLE_MODES.includes(styleMode) ? styleMode : null;
  if (!mode) return { basemapId: null, hint: '未知样式模式' };

  if (mode === 'hybrid') {
    const hybrid = resolveHybridPresentation(currentBasemapId, settings);
    return {
      basemapId: hybrid.basemapId,
      hint: hybrid.hint,
      extraOverlayId: hybrid.extraOverlayId || null,
      enableTerrain: true
    };
  }

  const family = getBasemapFamily(currentBasemapId);
  const picked = pickUsableCandidate(familyModeCandidates(family, mode), settings);
  if (picked.ok) {
    return { basemapId: picked.item.id, extraOverlayId: null, enableTerrain: false };
  }
  if (picked.hint) {
    return { basemapId: null, hint: picked.hint };
  }

  return {
    basemapId: null,
    hint: `${getBasemapFamilyLabel(family)}暂不支持该样式`
  };
}

export function syncOverlaysForStyleMode(currentBasemapId, styleMode, currentIds, settings) {
  const resolved = resolveBasemapIdForStyleMode(currentBasemapId, styleMode, settings);
  const keepId = resolved.extraOverlayId || null;
  const roadIds = allRoadOverlayIds();
  const next = (currentIds || []).filter((id) => !roadIds.includes(id) || id === keepId);
  if (keepId && !next.includes(keepId)) next.push(keepId);
  // 混合：默认叠天地图中文注记（卫星平面不自动勾）
  const tiandituLabelId = 'overlay-tianditu-cva';
  if (
    styleMode === 'hybrid'
    && !next.includes(tiandituLabelId)
    && isProviderEnabled(settings, tiandituLabelId)
    && canUseProvider(tiandituLabelId, settings)
  ) {
    next.push(tiandituLabelId);
  }
  // 所有底图默认叠天地图官方国界（省级以上境界）
  const boundaryId = 'overlay-tianditu-ibo';
  if (
    !next.includes(boundaryId)
    && isProviderEnabled(settings, boundaryId)
    && canUseProvider(boundaryId, settings)
  ) {
    next.push(boundaryId);
  }
  return next;
}

export function resolveFirstAvailableStyleMode(currentBasemapId, settings) {
  const order = ['streets', 'satellite', 'hybrid', 'outdoors', 'terrain'];
  for (let i = 0; i < order.length; i += 1) {
    const resolved = resolveBasemapIdForStyleMode(currentBasemapId, order[i], settings);
    if (resolved.basemapId) {
      return { styleMode: order[i], ...resolved };
    }
  }
  return { styleMode: null, basemapId: null };
}

/** 当前图商族下，各快捷样式是否可用 */
export function getStyleModeAvailability(currentBasemapId, settings) {
  const result = {};
  ALL_BASEMAP_STYLE_MODES.forEach((mode) => {
    const resolved = resolveBasemapIdForStyleMode(currentBasemapId, mode, settings);
    result[mode] = {
      available: !!resolved.basemapId,
      basemapId: resolved.basemapId || null,
      hint: resolved.hint || '',
      extraOverlayId: resolved.extraOverlayId || null,
      enableTerrain: Boolean(resolved.enableTerrain)
    };
  });
  return result;
}
